import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Star, Send, Loader2, MessageCircle, TrendingUp, DollarSign, Clock, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';

const MAX_CHARS = 500;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/* ── Stat Card ── */
const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div
    className="p-5 rounded-arch-lg flex items-center gap-4"
    style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
  >
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
      style={{ background: `${accent}18` }}
    >
      <Icon className="w-6 h-6" style={{ color: accent }} aria-hidden="true" />
    </div>
    <div>
      <p className="text-xs text-text-muted uppercase tracking-wider">{label}</p>
      <p className="font-heading text-2xl font-semibold text-text-main mt-0.5">{value}</p>
    </div>
  </div>
);

/* ── Thread View ── */
const ThreadView = ({ menteeId, menteeName, menteeAvatar, mentorId, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const loadThread = useCallback(async () => {
    try {
      const r = await api.mentorHubThread(menteeId);
      setMessages(r.data || []);
    } catch { setMessages([]); }
    setLoading(false);
  }, [menteeId]);

  useEffect(() => { loadThread(); }, [loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const [sendError, setSendError] = useState('');
  const lastMessage = messages[messages.length - 1];
  const canReply = !!lastMessage && lastMessage.sender_id !== mentorId;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    setSendError('');
    try {
      await api.mentorHubReply(menteeId, text);
      setDraft('');
      await loadThread();
    } catch (err) {
      setSendError(err?.response?.data?.detail || 'Failed to send reply. Please try again.');
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const [declining, setDeclining] = useState(false);
  const handleDecline = async () => {
    if (!window.confirm(`Decline this question from ${menteeName}? They'll be free to ask another mentor right away, and this won't count against them or affect your stats.`)) return;
    setDeclining(true);
    setSendError('');
    try {
      await api.mentorHubDecline(menteeId);
      await loadThread();
    } catch (err) {
      setSendError(err?.response?.data?.detail || 'Failed to decline. Please try again.');
    }
    setDeclining(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="p-4 border-b flex items-center gap-3" style={{ borderColor: 'var(--border)' }}>
        <button
          onClick={onBack}
          className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
          aria-label="Back to conversations"
          data-testid="thread-back-btn"
        >
          <ChevronLeft className="w-5 h-5 text-primary" aria-hidden="true" />
        </button>
        <img
          src={menteeAvatar || defaultAvatar()}
          alt={menteeName}
          className="w-9 h-9 rounded-full object-cover border"
          style={{ borderColor: 'var(--border)' }}
        />
        <span className="font-medium text-text-main">{menteeName}</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {loading && (
          <div className="flex items-center justify-center py-8" aria-live="polite">
            <Loader2 className="w-6 h-6 animate-spin text-primary" aria-hidden="true" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <p className="text-center text-sm text-text-muted py-8">No messages yet.</p>
        )}
        {messages.map((m) => {
          if (m.is_decline) {
            return (
              <div key={m.id} className="flex justify-center">
                <p className="text-xs italic text-text-muted px-3 py-1.5 rounded-arch" style={{ background: 'rgba(0,0,0,0.03)' }}>
                  You declined to answer this question · {formatTime(m.created_at)}
                </p>
              </div>
            );
          }
          const isMe = m.sender_id === mentorId;
          return (
            <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-[80%] px-4 py-2.5 rounded-arch text-sm leading-relaxed"
                style={isMe
                  ? { background: 'var(--primary)', color: 'var(--primary-foreground)' }
                  : { background: 'rgba(74,173,168,0.1)', color: 'var(--text-main)', border: '1px solid rgba(74,173,168,0.2)' }}
              >
                <p>{m.content}</p>
                <p className="text-[10px] mt-1 opacity-60">{formatTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Compose */}
      <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
        {sendError && (
          <p className="text-xs text-red-600 dark:text-red-400 mb-2" role="alert">{sendError}</p>
        )}
        {!canReply && !loading && (
          <p className="text-xs text-text-muted mb-2">
            No pending question from {menteeName} right now — you'll be notified when they ask a new one.
          </p>
        )}
        <div className="flex gap-3 items-end">
          <div className="flex-1 relative">
            <label htmlFor="hub-reply-input" className="sr-only">Reply to {menteeName}</label>
            <textarea
              id="hub-reply-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_CHARS))}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder={canReply ? "Write your answer..." : "No pending question to answer"}
              disabled={!canReply}
              className="w-full px-4 py-3 rounded-arch text-sm resize-none focus:ring-2 focus:ring-primary transition-all disabled:opacity-50"
              style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
              data-testid="hub-reply-input"
            />
            <span
              className="absolute bottom-3 right-3 text-[10px] pointer-events-none"
              style={{ color: draft.length >= MAX_CHARS ? '#dc2626' : 'var(--text-muted)' }}
              aria-live="polite"
              aria-label={`${draft.length} of ${MAX_CHARS} characters`}
            >
              {draft.length}/{MAX_CHARS}
            </span>
          </div>
          <button
            onClick={handleDecline}
            disabled={declining || sending || !canReply}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-arch transition-colors disabled:opacity-40"
            style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.2)' }}
            aria-label="Decline this question"
            title="Decline — won't count against you or the member"
            data-testid="hub-decline-btn"
          >
            {declining
              ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              : <XCircle className="w-5 h-5" aria-hidden="true" />}
          </button>
          <button
            onClick={handleSend}
            disabled={sending || declining || !draft.trim() || !canReply}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-arch transition-colors disabled:opacity-40"
            style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
            aria-label="Send reply"
            data-testid="hub-send-btn"
          >
            {sending
              ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
              : <Send className="w-5 h-5" aria-hidden="true" />}
          </button>
        </div>
        {canReply && (
          <p className="text-[11px] text-text-muted mt-1.5">
            Not comfortable answering this one? Decline — it won't count against you or {menteeName}.
          </p>
        )}
      </div>
    </div>
  );
};


/* ════ MENTOR HUB PAGE ════ */
const MentorHubPage = ({ onBack }) => {
  const { profile } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeThread, setActiveThread] = useState(null); // { menteeId, menteeName, menteeAvatar }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [convsRes, statsRes] = await Promise.all([
        api.mentorHubConversations(),
        api.mentorHubStats(),
      ]);
      setConversations(convsRes.data || []);
      setStats(statsRes.data || null);
    } catch { /* show empty state */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (!profile?.is_mentor) {
    return (
      <div className="max-w-3xl mx-auto p-4 lg:p-8">
        <div className="p-8 text-center rounded-arch-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <p className="text-text-muted">This page is only accessible to Sirati Mentors.</p>
        </div>
      </div>
    );
  }

  const isThreadOpen = !!activeThread;

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-arch-xl overflow-hidden"
        style={{ background: 'var(--card)', border: '1px solid var(--border)', minHeight: '600px' }}
      >
        {isThreadOpen ? (
          <div className="flex flex-col" style={{ minHeight: '600px' }}>
            <ThreadView
              menteeId={activeThread.menteeId}
              menteeName={activeThread.menteeName}
              menteeAvatar={activeThread.menteeAvatar}
              mentorId={profile.id}
              onBack={() => { setActiveThread(null); loadData(); }}
            />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 border-b flex items-center gap-4" style={{ borderColor: 'var(--border)' }}>
              <button
                onClick={onBack}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
                aria-label="Go back"
                data-testid="mentor-hub-back-btn"
              >
                <ChevronLeft className="w-6 h-6 text-primary" aria-hidden="true" />
              </button>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-primary flex items-center gap-2">
                  <Star className="w-6 h-6 text-yellow-600" aria-hidden="true" />
                  Mentor Hub
                </h1>
                <p className="text-sm text-text-muted mt-0.5">Your mentee conversations and earnings</p>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Stats */}
              {stats && (
                <div className="grid grid-cols-2 gap-4">
                  <StatCard
                    icon={MessageCircle}
                    label="Questions Answered This Month"
                    value={stats.questions_this_month ?? stats.conversations_this_month ?? 0}
                    accent="#4AADA8"
                  />
                  <StatCard
                    icon={DollarSign}
                    label="Est. Earnings"
                    value={`$${stats.estimated_earnings.toFixed(2)}`}
                    accent="#92680a"
                  />
                </div>
              )}

              {/* Earnings note */}
              <div
                className="flex items-start gap-3 p-4 rounded-arch text-xs text-text-muted leading-relaxed"
                style={{ background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)' }}
              >
                <TrendingUp className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" aria-hidden="true" />
                <p>
                  Estimated earnings are calculated at <strong className="text-text-main">$2.00 per question answered</strong>. Payouts are processed monthly.
                </p>
              </div>

              {/* Conversations */}
              <div>
                <h2 className="font-heading text-lg font-medium text-text-main mb-3">Active Conversations</h2>

                {loading && (
                  <div className="flex items-center justify-center py-10" aria-live="polite">
                    <Loader2 className="w-7 h-7 animate-spin text-primary" aria-hidden="true" />
                  </div>
                )}

                {!loading && conversations.length === 0 && (
                  <div className="text-center py-10 text-text-muted">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" aria-hidden="true" />
                    <p className="text-sm">No messages yet. When mentees reach out, their conversations will appear here.</p>
                  </div>
                )}

                {!loading && conversations.length > 0 && (
                  <ul className="space-y-3" aria-label="Mentee conversations">
                    {conversations.map((conv) => (
                      <li key={conv.mentee?.id}>
                        <button
                          onClick={() => setActiveThread({
                            menteeId: conv.mentee.id,
                            menteeName: conv.mentee.display_name,
                            menteeAvatar: conv.mentee.avatar_url,
                          })}
                          className="w-full text-left p-4 rounded-arch-lg flex items-center gap-4 hover:bg-primary/5 transition-colors"
                          style={{ border: '1px solid var(--border)' }}
                          data-testid={`hub-conv-${conv.mentee?.id}`}
                        >
                          <div className="relative shrink-0">
                            <img
                              src={conv.mentee?.avatar_url || defaultAvatar()}
                              alt={conv.mentee?.display_name}
                              className="w-11 h-11 rounded-full object-cover border"
                              style={{ borderColor: 'var(--border)' }}
                            />
                            {conv.unread_count > 0 && (
                              <span
                                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                                style={{ background: '#4AADA8' }}
                                aria-label={`${conv.unread_count} unread`}
                              >
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-text-main text-sm truncate">
                                {conv.mentee?.display_name}
                              </span>
                              <span className="text-[10px] text-text-muted shrink-0 flex items-center gap-1">
                                <Clock className="w-3 h-3" aria-hidden="true" />
                                {formatTime(conv.last_message_at)}
                              </span>
                            </div>
                            {conv.last_message && (
                              <p className="text-xs text-text-muted truncate mt-0.5">{conv.last_message}</p>
                            )}
                          </div>
                          <ChevronLeft className="w-4 h-4 text-text-muted rotate-180 shrink-0" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default MentorHubPage;
